import { safeLocalStorageGet, safeLocalStorageRemove, safeLocalStorageSet } from "./storage.js";

// Offline-fallback cache for API reads. Pages do NOT hydrate from it — they
// show loading skeletons while the fetch runs (see CatalogPage/ProfilePage).
// The cache is served only when the request got no usable answer: a network
// failure, a timed-out request, or a transient server-side status (5xx/429/408
// — a cold-starting proxy answers 502 for the very outage this cache exists to
// cover). A definitive denial (401/403/404/410) evicts the cached copy
// instead, and the whole cache is dropped whenever the acting identity changes
// so one account's private data never survives into another session on a
// shared browser. Writes go through safeLocalStorage*, so quota/private-mode
// failures silently disable caching without breaking the app.

const PREFIX = "lorevok:cache:";
let revision = 0;
let identityRevision = 0;
export function apiCacheRevision(): number { return revision; }
export function apiIdentityRevision(): number { return identityRevision; }
export function invalidateReadMemory(): void { revision += 1; }

export const CATALOG_CACHE_KEY = "catalog";

export function myNovelsCacheKey(ownerId: string): string {
  return `myNovels:${ownerId}`;
}

function novelCacheKey(id: string): string {
  return `novel:${id}`;
}

/** Records which identity wrote the cache, so a change of account clears it. */
const OWNER_MARKER_KEY = "cache-owner";

/** Full novels are heavy (100-300KB); keep only the most recently used few. */
const NOVEL_LRU_INDEX = "novel-lru";
const NOVEL_LRU_MAX = 10;

export function readApiCache<T>(key: string): T | null {
  const raw = safeLocalStorageGet(PREFIX + key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeApiCache(key: string, value: unknown): void {
  try {
    safeLocalStorageSet(PREFIX + key, JSON.stringify(value));
  } catch {
    // Value not serializable — skip caching rather than break the caller.
  }
}

export function removeApiCache(key: string): void {
  safeLocalStorageRemove(PREFIX + key);
}

/**
 * Caches a full novel entry ({ novel, stats }), evicting the least recently
 * used entries beyond NOVEL_LRU_MAX so heavy documents can't silently exhaust
 * the ~5MB quota (which would also break player progress saves on the same
 * origin).
 */
export function writeNovelCache(id: string, entry: unknown): void {
  const index = readApiCache<string[]>(NOVEL_LRU_INDEX) ?? [];
  const next = [id, ...index.filter((item) => item !== id)];
  for (const evicted of next.slice(NOVEL_LRU_MAX)) {
    removeApiCache(novelCacheKey(evicted));
  }
  writeApiCache(NOVEL_LRU_INDEX, next.slice(0, NOVEL_LRU_MAX));
  writeApiCache(novelCacheKey(id), entry);
}

/**
 * Reads a cached novel entry. Entries written before stats were cached (a
 * bare Novel instead of { novel, stats }) are treated as misses.
 */
export function readNovelCache<T extends { novel: unknown }>(id: string): T | null {
  const cached = readApiCache<T>(novelCacheKey(id));
  return cached !== null && typeof cached === "object" && "novel" in cached ? cached : null;
}

export function removeNovelCache(id: string): void {
  invalidateReadMemory();
  removeApiCache(novelCacheKey(id));
  const index = readApiCache<string[]>(NOVEL_LRU_INDEX) ?? [];
  writeApiCache(NOVEL_LRU_INDEX, index.filter((item) => item !== id));
}

/**
 * Drops the catalog and every my-novels list. Called after content mutations
 * (save/publish/delete) — a later offline hydration must not resurrect a
 * deleted novel or an outdated list.
 */
export function invalidateListCaches(): void {
  invalidateReadMemory();
  removeApiCache(CATALOG_CACHE_KEY);
  for (const key of listCacheKeys(PREFIX + "myNovels:")) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing cached, nothing to remove.
    }
  }
}

/**
 * Clears the cache when the acting identity changes (sign-in as someone else,
 * sign-out, an expired session restoring as anonymous) and records the new
 * owner. A no-op while the identity is stable, so routine SIGNED_IN events
 * (supabase re-confirms the session on tab refocus) don't wipe a warm cache.
 */
export function syncCacheOwner(ownerId: string | null): void {
  const next = ownerId ?? "anon";
  if (safeLocalStorageGet(PREFIX + OWNER_MARKER_KEY) === next) return;
  clearApiCache();
  safeLocalStorageSet(PREFIX + OWNER_MARKER_KEY, next);
}

/** Drops every cached API response. */
export function clearApiCache(): void {
  identityRevision += 1;
  invalidateReadMemory();
  for (const key of listCacheKeys(PREFIX)) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing cached, nothing to clear.
    }
  }
}

function listCacheKeys(prefix: string): string[] {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
    return keys;
  } catch {
    return [];
  }
}
